import {PostgrestFilterBuilder} from '@supabase/postgrest-js'
import {ok, err, type Res, type Err, type OK, isErr} from 'shared/promise'

type InferQueryData<TQuery extends {range: (...args: any) => PromiseLike<any>}> =
  ReturnType<TQuery['range']> extends PromiseLike<{data: (infer U)[] | null; error: any}> ? U : never

/**
 * Configuration options for the Supabase query iterator.
 */
interface SupabaseIteratorOptions {
  /**
   * The number of rows to fetch per page request.
   * @default 1000
   */
  pageSize?: number
}

/**
 * Creates a generic async iterator for a Supabase query that supports paging.
 *
 * This allows you to process large datasets efficiently without loading all
 * rows into memory at once. It automatically infers the row type from the
 * Supabase query builder you provide.
 *
 * @template TQuery - The type of the Supabase query builder.
 *   This is inferred automatically.
 * @param {TQuery} queryBuilder - The Supabase query builder instance,
 *   typically the result of `supabase.from('...').select('...')`.
 * @param {SupabaseIteratorOptions} [options={}] - Configuration options for paging.
 * @returns {AsyncIterable<OK<InferQueryData<TQuery>> | Err<{message: string}>>} An async iterable that
 *   yields one row at a time wrapped in ok() or error objects.
 *
 * @example
 * const query = supabase.from('posts').select('id, title');
 *
 * for await (const result of sbIterator(query, { pageSize: 50 })) {
 *   if (!result.ok) return result
 *   console.log(`Processing post: ${result.title}`);
 * }
 */
export async function* sbIteratorByPage<TQuery extends PostgrestFilterBuilder<any, any, any, any>>(
  queryBuilder: TQuery,
  options: SupabaseIteratorOptions = {}
): AsyncIterable<OK<InferQueryData<TQuery>[]> | Err<{message: string}>> {
  const {pageSize = 1000} = options
  let currentPage = 0
  while (true) {
    const from = currentPage * pageSize
    const to = from + pageSize - 1
    const {data, error} = await queryBuilder.range(from, to)
    if (error) {
      yield err(error.message)
      return
    }
    if (data && data.length > 0) {
      yield ok(data as InferQueryData<TQuery>[])
      if (data.length < pageSize) return
    } else return
    currentPage++
  }
}

export async function* sbIteratorByRow<TQuery extends PostgrestFilterBuilder<any, any, any, any>>(
  queryBuilder: TQuery,
  options: SupabaseIteratorOptions = {}
): AsyncIterable<OK<InferQueryData<TQuery>> | Err<{message: string}>> {
  for await (const result of sbIteratorByPage(queryBuilder, options)) {
    if (!result.ok) yield result
    else for (const row of result) yield ok(row)
  }
}

// /**
//  * Factory function to create a Supabase iterator with validation.
//  * Returns an ok/err result containing the iterator or an error.
//  *
//  * @template TQuery - The type of the Supabase query builder.
//  * @param {TQuery} queryBuilder - The Supabase query builder instance.
//  * @param {SupabaseIteratorOptions} [options={}] - Configuration options for paging.
//  * @returns {Res<AsyncIterable<OK<InferQueryData<TQuery>> | Err<{message: string}>>, {message: string}>}
//  *
//  * @example
//  * const iteratorResult = createSbIterator(query)
//  * if (!iteratorResult.ok) return iteratorResult
//  *
//  * for await (const result of iteratorResult) {
//  *   if (!result.ok) return result
//  *   console.log('Processing:', result)
//  * }
//  */
// export const createSbIterator = <TQuery extends PostgrestFilterBuilder<any, any, any>>(
//   queryBuilder: TQuery,
//   options: SupabaseIteratorOptions = {}
// ): Res<AsyncIterable<OK<InferQueryData<TQuery>[]> | Err<{message: string}>>, {message: string}> => {
//   if (!queryBuilder || typeof queryBuilder.range !== 'function') return err({message: 'Invalid query builder provided'})
//   return ok(sbIterator(queryBuilder, options))
// }

/**
 * Fetches all rows from a Supabase query as an array.
 *
 * This is a convenience function that uses the `sbIterator` to fetch all rows
 * from a Supabase query and returns them as a single array. It automatically
 * handles paging to ensure all data is retrieved, regardless of the total
 * number of rows.
 *
 * Use this when you need all the data at once and don't want to process it
 * row by row. For large datasets, consider using `sbIterator` directly to
 * avoid loading everything into memory simultaneously.
 *
 * @template TQuery - The type of the Supabase query builder.
 *   This is inferred automatically.
 * @param {TQuery} queryBuilder - The Supabase query builder instance,
 *   typically the result of `supabase.from('...').select('...')`.
 * @param {SupabaseIteratorOptions} [options={}] - Configuration options for paging.
 * @returns {Promise<Res<InferQueryData<TQuery>[], {message: string}>>} A promise that resolves to an ok/err result
 *   containing an array of all rows from the query or an error.
 *
 * @example
 * const query = supabase.from('users').select('id, name, email');
 *
 * const allUsersResult = await sbAll(query);
 * if (!allUsersResult.ok) return allUsersResult;
 * console.log(`Found ${allUsersResult.length} users`);
 *
 * allUsersResult.forEach(user => {
 *   console.log(`User: ${user.name} (${user.email})`);
 * });
 *
 * @example
 * // With custom page size for better performance
 * const largeQuery = supabase.from('posts').select('*');
 * const allPostsResult = await sbAll(largeQuery, { pageSize: 500 });
 * if (!allPostsResult.ok) return allPostsResult;
 * console.log(`Retrieved ${allPostsResult.length} posts`);
 */
export const sbAll = async <TQuery extends PostgrestFilterBuilder<any, any, any, any>>(
  queryBuilder: TQuery,
  options: SupabaseIteratorOptions = {}
): Promise<OK<InferQueryData<TQuery>[]> | Err> => {
  const results: InferQueryData<TQuery>[] = []
  for await (const result of sbIteratorByPage(queryBuilder, options)) {
    if (!result.ok) return result
    results.push(...result)
  }
  return ok(results)
}

/**
 * Creates an async iterator that groups rows from a Supabase query by a specified column.
 *
 * This function processes a Supabase query and yields groups of rows where each group
 * contains all rows that share the same value in the specified grouping column. The
 * query is automatically ordered by the grouping column to ensure proper grouping.
 *
 * This is useful for processing data that needs to be grouped for analysis, reporting,
 * or batch processing operations.
 *
 * @template TQuery - The type of the Supabase query builder.
 *   This is inferred automatically.
 * @param {TQuery} queryBuilder - The Supabase query builder instance,
 *   typically the result of `supabase.from('...').select('...')`.
 * @param {string} groupColumn - The name of the column to group by.
 *   The query will be automatically ordered by this column.
 * @returns {AsyncIterableIterator<[string, InferQueryData<TQuery>[]] | Err<{message: string}>>} An async iterable that yields tuples of
 *   [groupValue, rows] where groupValue is the value of the grouping column and rows
 *   is an array of all rows that belong to that group, or error objects.
 *
 * @example
 * // Assumes `supabase` is your initialized Supabase client.
 * const query = supabase.from('orders').select('id, customer_id, amount, status');
 *
 * for await (const result of sbGroupIterator(query, 'customer_id')) {
 *   if (!result.ok) return result
 *   const [customerId, orders] = result;
 *   // `customerId` is the customer ID string
 *   // `orders` is an array of order objects for that customer
 *   console.log(`Customer ${customerId} has ${orders.length} orders`);
 *
 *   const totalAmount = orders.reduce((sum, order) => sum + order.amount, 0);
 *   console.log(`Total amount: $${totalAmount}`);
 * }
 *
 * @example
 * // Group by status to process orders by their current state
 * const ordersQuery = supabase.from('orders').select('*');
 *
 * for await (const result of sbGroupIterator(ordersQuery, 'status')) {
 *   if (!result.ok) return result
 *   const [status, orders] = result;
 *   switch (status) {
 *     case 'pending':
 *       await processPendingOrders(orders);
 *       break;
 *     case 'shipped':
 *       await updateShippedOrders(orders);
 *       break;
 *   }
 * }
 */
export async function* sbGroupIterator<TQuery extends PostgrestFilterBuilder<any, any, any, any>>(
  queryBuilder: TQuery,
  groupColumn: string
): AsyncIterableIterator<OK<[string, InferQueryData<TQuery>[]]> | Err<{message: string}>> {
  const iterator = sbIteratorByPage(queryBuilder.order(groupColumn))
  let currentGroup: string | null = null
  let currentRows: InferQueryData<TQuery>[] = []
  for await (const pageRows of iterator) {
    if (!pageRows.ok) {
      yield pageRows
      return
    }
    for (const row of pageRows) {
      const dataRow = row
      const groupValue = (dataRow as any)[groupColumn]
      if (currentGroup == groupValue) {
        currentRows.push(dataRow)
        continue
      }
      if (currentGroup && currentRows.length > 0) yield ok([currentGroup, currentRows])
      currentGroup = groupValue
      currentRows = [dataRow]
    }
  }
  if (currentGroup && currentRows.length > 0) yield ok([currentGroup, currentRows])
}
